import * as React from 'react';

import { translate } from '~l10n/client';
import { useDispatch } from 'react-redux';

import { ImageUploadContext } from '~analytics/adapters/ImageEventAdapterTypes';
import { SmallButton } from '~shared/components/buttons';
import { IconPlus } from '~shared/components/icons/standard';
import ImageFieldDisplay from '~shared/components/ImageField/ImageFieldDisplay';
import QuillEditor from '~shared/components/quill/QuillEditor';
import { QuillTextChangeHandler } from '~shared/components/quill/quillHooks';
import { ImageResource, QuizAnswer } from '~teacher/models/quizzes';

import {
  changeAnswerText,
  deleteAnswer,
  updateAnswerImage,
} from '../../slices/quiz/quizSlice';

function QuestionMatchingAnswerEdit({
  answerId,
  answerText,
  answerImage,
  isDisabled,
}: {
  answerId: string;
  answerText: string;
  answerImage: ImageResource;
  isDisabled: boolean;
}) {
  const dispatch = useDispatch();

  const handleTextChange = React.useCallback<QuillTextChangeHandler>(
    ({ html }: { html: string; text: string }) => {
      const answerText = html;
      dispatch(changeAnswerText({ answerId: answerId, answerText }));
    },
    [dispatch, answerId]
  );

  const handleImageChange = React.useCallback(
    (url) => {
      dispatch(updateAnswerImage({ answerId: answerId, url }));
    },
    [dispatch, answerId]
  );

  return (
    <QuillEditor
      initialHtml={answerText}
      onTextChange={handleTextChange}
      hasImageField
      isReadOnly={false}
      isImageSet={!!answerImage}
    >
      <ImageFieldDisplay
        context={ImageUploadContext.Answer}
        onImageChange={handleImageChange}
        isReadOnly={isDisabled}
        image={answerImage}
        imageFieldLabel={translate('Answer Image')}
        imageClearLabel={translate('Remove Image')}
        imageFieldDisabled={isDisabled}
      />
    </QuillEditor>
  );
}

export function QuestionMatchingAnswerPairEdit({
  answerPrompt,
  answerMatch,
  isDisabled,
}: {
  answerPrompt: QuizAnswer;
  answerMatch: QuizAnswer;
  isDisabled: boolean;
}) {
  const dispatch = useDispatch();
  const deletePair = React.useCallback(() => {
    if (answerPrompt) {
      dispatch(deleteAnswer({ answerId: answerPrompt.id }));
    }
    if (answerMatch) {
      dispatch(deleteAnswer({ answerId: answerMatch.id }));
    }
  }, [dispatch, answerPrompt, answerMatch]);

  return (
    <div>
      <div>
        Prompt
        {answerPrompt && (
          <QuestionMatchingAnswerEdit
            answerId={answerPrompt.id as string}
            answerText={answerPrompt.text}
            answerImage={answerPrompt.image}
            isDisabled={isDisabled}
          />
        )}
      </div>
      <div>
        Match
        {answerMatch && (
          <QuestionMatchingAnswerEdit
            answerId={answerMatch.id as string}
            answerText={answerMatch.text}
            answerImage={answerMatch.image}
            isDisabled={isDisabled}
          />
        )}
      </div>
      <div className="mt-4">
        <SmallButton
          isOnWhite={false}
          onClick={deletePair}
          isDisabled={isDisabled}
        >
          <span className="inline-flex mr-1 -ml-2 text-inherit">
            <IconPlus />
          </span>
          Delete Pair
        </SmallButton>
      </div>
    </div>
  );
}
